import { Center, forwardRef, Input, PopoverTrigger, Spacer, useFormControl, useToken, VisuallyHidden } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '@bonusly/ds-button';
import { XCircleOutline } from '@bonusly/ds-icon';
import { Tooltip } from '@bonusly/ds-tooltip';
import { rem } from '@bonusly/ds-utils';

import { assertIsDateRange, DateValue } from '../../types';
import { useDatePickerContext } from '../context';

type DateRangeInputProps = ButtonProps & {
	children?: ReactNode | ((props: { value: [DateValue, DateValue] }) => ReactNode);
};

export const DateRangeInput = forwardRef<DateRangeInputProps, 'button'>(({ children, ...rest }, ref) => {
	const errorColor = useToken('colors', 'error.inputBorder');
	const formProps = useFormControl({ readOnly: true });
	const { t } = useTranslation('components');
	const { displayFormat, isClearable, onClear, onOpen, value } = useDatePickerContext();

	assertIsDateRange(value);

	const isEmpty = !value.some(Boolean);

	const renderedChildren = (() => {
		if (typeof children === 'function') {
			return children({ value });
		}

		if (children) {
			return children;
		}

		if (isEmpty) {
			return t('date_picker.range_placeholder');
		}

		return value
			.map((date) => {
				return date
					? t('date_picker.date_value', {
							context: displayFormat,
							date,
							interpolation: { escapeValue: false },
						})
					: '';
			})
			.join(' - ');
	})();

	return (
		<>
			<VisuallyHidden>
				<Input
					value={value
						.map((v) => (v ? v.toISOString() : null))
						.filter(Boolean)
						.join('-')}
					{...formProps}
				/>
			</VisuallyHidden>
			<PopoverTrigger>
				<Button
					bg="bg.surface"
					borderColor="border.default"
					borderStyle="solid"
					borderWidth="1px"
					color={!isEmpty ? 'text.default' : 'text.softest'}
					justifyContent="flex-start"
					fontWeight="normal"
					minH={rem(40)}
					minW={rem(192)}
					ref={ref}
					onClick={onOpen}
					px={3}
					py={2}
					_active={{
						borderColor: 'border.focus',
						outline: '1px solid',
						outlineOffset: '0',
						_disabled: {
							borderColor: 'border.default',
							outline: 'none',
							outlineOffset: '0',
						},
					}}
					_hover={{
						bg: 'bg.soft',
					}}
					_invalid={{
						borderColor: 'error.inputBorder',
						boxShadow: `0 0 0 1px ${errorColor}`,
					}}
					{...formProps}
					{...rest}
					{...(value &&
						isClearable &&
						!isEmpty && {
							rightIcon: (
								<Tooltip label={t('clear')} placement="bottom">
									<Center
										tabIndex={0}
										role="button"
										onClick={(e) => {
											e.stopPropagation();
											onClear();
										}}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.stopPropagation();
												onClear();
											}
										}}
									>
										<XCircleOutline
											color="text.softest"
											aria-label={t('clear')}
											ml={2}
											size="md"
											_hover={{
												color: 'text.default',
											}}
										/>
									</Center>
								</Tooltip>
							),
						})}
				>
					{renderedChildren}
					<Spacer />
				</Button>
			</PopoverTrigger>
		</>
	);
});

DateRangeInput.displayName = 'DateRangeInput';
